function initHeroSlider() {
  const section = document.querySelector('[data-section-type="hero-slider"]');
  if (!section) return;

  const slider = section.querySelector('[data-hero-slider]');
  const track = section.querySelector('[data-hero-slider-track]');
  const prevBtn = section.querySelector('[data-hero-slider-prev]');
  const nextBtn = section.querySelector('[data-hero-slider-next]');
  const dots = section.querySelectorAll('[data-hero-slider-dot]');

  if (!slider || !track) return;

  const realSlides = Array.from(track.children).filter((child) => child.hasAttribute('data-hero-slide'));
  const count = realSlides.length;
  if (count <= 1) return;

  const DURATION = 300;
  const TIMING = 'ease';
  const DIM_OPACITY = '0.45';

  // Infinite-loop peek: clone the last slide before the first and the first after the last, so the
  // edges always have a neighbour to peek. DOM order becomes [lastClone, real0..realN-1, firstClone].
  const makeClone = (source) => {
    const clone = source.cloneNode(true);
    clone.setAttribute('data-hero-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('tabindex', '-1');
    return clone;
  };
  track.insertBefore(makeClone(realSlides[count - 1]), realSlides[0]);
  track.appendChild(makeClone(realSlides[0]));

  const slides = Array.from(track.children).filter((child) => child.hasAttribute('data-hero-slide'));
  const total = slides.length; // count + 2

  // `position` indexes `slides` (clones included). Real slide r lives at position r + 1.
  // Start on the first real slide (position 1) so the last image peeks on its left.
  let position = 1;
  let autoplayId = null;

  const maxSlideHeight = () => window.innerHeight * 0.6;
  // Flex `gap` is NOT counted in offsetWidth, so it must be added into the centering math explicitly.
  const gapWidth = () => parseFloat(getComputedStyle(track).columnGap) || 0;

  const layout = () => {
    const containerWidth = slider.clientWidth;
    const maxHeight = maxSlideHeight();

    for (const slide of slides) {
      const img = slide.querySelector('img');
      if (!img) continue;

      const widthAttr = img.getAttribute('width');
      const heightAttr = img.getAttribute('height');
      let ratio = 0;

      if (widthAttr && heightAttr && Number(heightAttr) > 0) {
        ratio = Number(widthAttr) / Number(heightAttr);
      } else if (img.naturalWidth && img.naturalHeight) {
        ratio = img.naturalWidth / img.naturalHeight;
      }

      if (ratio > 0) {
        const desiredWidth = Math.min(containerWidth, maxHeight * ratio);
        slide.style.width = `${desiredWidth}px`;
      }
    }
  };

  const realIndex = () => (((position - 1) % count) + count) % count;

  // The translateX (px) that centres the slide at `pos`. Flex `gap` is NOT counted in offsetWidth,
  // so one gap per preceding slide is added explicitly.
  const computeTranslate = (pos) => {
    const containerWidth = slider.clientWidth;
    const gap = gapWidth();
    let offsetLeft = 0;
    for (let i = 0; i < pos; i++) {
      offsetLeft += slides[i].offsetWidth + gap;
    }
    const activeWidth = slides[pos].offsetWidth;
    const centerOffset = (containerWidth - activeWidth) / 2;
    return centerOffset - offsetLeft;
  };

  const render = (animate = true) => {
    const translate = computeTranslate(position);

    track.style.transition = animate ? `transform ${DURATION}ms ${TIMING}` : 'none';
    track.style.transform = `translateX(${translate}px)`;

    // Dim every non-active slide so the centred one reads as the main slide. Opacity transition
    // matches the transform duration so the fade tracks the slide movement. When animate is false
    // (initial paint and the wrap snap) the transition is off, so the snap never flashes.
    for (let i = 0; i < slides.length; i++) {
      slides[i].style.transition = animate ? `opacity ${DURATION}ms ${TIMING}` : 'none';
      slides[i].style.opacity = i === position ? '1' : DIM_OPACITY;
    }

    const active = realIndex();
    for (const dot of dots) {
      const index = Number(dot.dataset.index);
      dot.classList.toggle('bg-dragon-primary', index === active);
      dot.classList.toggle('bg-black/30', index !== active);
    }
  };

  const goTo = (nextPosition) => {
    position = nextPosition;
    render(true);
  };

  const next = () => goTo(position + 1);
  const prev = () => goTo(position - 1);

  const startAutoplay = () => {
    autoplayId = setInterval(next, 5000);
  };

  const resetAutoplay = () => {
    clearInterval(autoplayId);
    startAutoplay();
  };

  // When a wrap animation lands on a clone, snap (transition off) to the matching real slide.
  // Both are the same image at the same on-screen spot, so with the transition disabled there is
  // no visible jump.
  track.addEventListener('transitionend', (event) => {
    if (event.target !== track || event.propertyName !== 'transform') return;
    if (position === total - 1) {
      position = 1; // firstClone -> first real slide
      render(false);
    } else if (position === 0) {
      position = count; // lastClone -> last real slide
      render(false);
    }
  });

  prevBtn?.addEventListener('click', () => {
    prev();
    resetAutoplay();
  });

  nextBtn?.addEventListener('click', () => {
    next();
    resetAutoplay();
  });

  for (const dot of dots) {
    dot.addEventListener('click', () => {
      goTo(Number(dot.dataset.index) + 1);
      resetAutoplay();
    });
  }

  // --- Pointer drag (touch + mouse) --------------------------------------------------------------
  // touch-action pan-y lets the browser keep vertical page scroll while we own horizontal drags.
  track.style.touchAction = 'pan-y';

  const DRAG_MOVE_THRESHOLD = 8; // px of movement before a press counts as a drag (vs a tap/click)
  const ADVANCE_RATIO = 0.2; // drag past 20% of the active slide width to change slide
  const FLICK_VELOCITY = 0.5; // px/ms; a fast flick changes slide even below the distance threshold

  let dragging = false;
  let dragMoved = false;
  let dragStartX = 0;
  let dragBase = 0;
  let dragStartTime = 0;
  let dragPointerId = null;
  let suppressClickUntil = 0;

  const currentTranslateX = () => {
    const transform = getComputedStyle(track).transform;
    if (!transform || transform === 'none') return 0;
    return new DOMMatrixReadOnly(transform).m41;
  };

  const onPointerMove = (event) => {
    if (!dragging || event.pointerId !== dragPointerId) return;
    const dx = event.clientX - dragStartX;
    if (!dragMoved && Math.abs(dx) > DRAG_MOVE_THRESHOLD) dragMoved = true;
    track.style.transform = `translateX(${dragBase + dx}px)`;
  };

  const onPointerEnd = (event) => {
    if (!dragging || event.pointerId !== dragPointerId) return;
    dragging = false;
    dragPointerId = null;
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', onPointerEnd);
    document.removeEventListener('pointercancel', onPointerEnd);

    if (event.type === 'pointercancel') {
      if (dragMoved) {
        suppressClickUntil = Date.now() + 100;
      }
      goTo(position);
      resetAutoplay();
      return;
    }

    const dx = event.clientX - dragStartX;
    let target = position;

    // Only treat the gesture as a drag (and suppress the follow-up click) if the pointer
    // moved more than the tap threshold. This keeps normal link taps working while still
    // allowing drags/flicks to advance the slider.
    if (dragMoved) {
      const velocity = dx / Math.max(1, performance.now() - dragStartTime);
      const distanceThreshold = slides[position].offsetWidth * ADVANCE_RATIO;

      if (dx <= -distanceThreshold || velocity <= -FLICK_VELOCITY) {
        target = position + 1; // dragged left -> next
      } else if (dx >= distanceThreshold || velocity >= FLICK_VELOCITY) {
        target = position - 1; // dragged right -> prev
      }

      suppressClickUntil = Date.now() + 100;
    }

    goTo(target); // animates from the current dragged offset to the centred target; clone snap still applies
    resetAutoplay();
  };

  const onPointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    dragging = true;
    dragMoved = false;
    suppressClickUntil = 0;
    dragStartX = event.clientX;
    dragStartTime = performance.now();
    // Freeze wherever the track currently is (handles grabbing mid-animation) and follow the finger.
    dragBase = currentTranslateX();
    track.style.transition = 'none';
    track.style.transform = `translateX(${dragBase}px)`;
    clearInterval(autoplayId); // pause autoplay while held
    dragPointerId = event.pointerId;
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerEnd);
    document.addEventListener('pointercancel', onPointerEnd);
  };

  track.addEventListener('pointerdown', onPointerDown);

  // Suppress the click that follows a drag so linked slides (<a data-hero-slide>) don't navigate.
  // A genuine tap never crosses the movement threshold, so its click passes through.
  slider.addEventListener(
    'click',
    (event) => {
      if (Date.now() < suppressClickUntil && event.target.closest('[data-hero-slide]')) {
        event.preventDefault();
        event.stopPropagation();
        suppressClickUntil = 0;
      }
    },
    true,
  );

  const handleResize = () => {
    layout();
    render(false);
  };

  window.addEventListener('resize', handleResize);

  for (const slide of slides) {
    const img = slide.querySelector('img');
    if (img) {
      if (img.complete) {
        layout();
        render(false);
      } else {
        img.addEventListener('load', () => {
          layout();
          render(false);
        });
      }
    }
  }

  layout();
  render(false);
  startAutoplay();

  slider.addEventListener('mouseenter', () => clearInterval(autoplayId));
  slider.addEventListener('mouseleave', startAutoplay);
}

document.addEventListener('DOMContentLoaded', initHeroSlider);
